import React, { useState, useEffect } from 'react';
import { 
  GitBranch, GitCommit, Play, RefreshCw, AlertTriangle, ShieldCheck, 
  Lock, Unlock, ChevronRight, CheckCircle2, Loader2, ArrowLeftRight,
  Info, FileText, Sparkles, X
} from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

interface Commit {
  sha: string;
  author: string;
  message: string;
  date: string;
}

interface Revision {
  name: string;
  active: boolean;
  trafficPercent: number;
  createTime: string;
  commitSha?: string;
  author?: string;
  message?: string;
}

interface VersionStatus {
  isProd: boolean;
  isLocked: boolean;
  currentProdCommit: Commit;
  currentStagingCommit: Commit;
  commitsBehind: number;
  dataSource?: string;
  metadataSource?: string;
}

interface CommitDetails {
  sha: string;
  author: string;
  message: string;
  date: string;
  files: Array<{
    path: string;
    name: string;
    oqueMudou: string;
    intencao: string;
  }>;
  resenha: {
    oqueAlterou: string;
    intencao: string;
    recomendacao: string;
  };
}

export function MasterVersionsPage() {
  const [status, setStatus] = useState<VersionStatus | null>(null);
  const [commits, setCommits] = useState<Commit[]>([]);
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(false);

  // Detalhes da Análise de Commit (IA)
  const [selectedCommitSha, setSelectedCommitSha] = useState<string | null>(null);
  const [commitDetails, setCommitDetails] = useState<CommitDetails | null>(null);
  const [loadingCommitDetails, setLoadingCommitDetails] = useState(false);

  // Análise em Lote Completa (Full Screen)
  const [batchAnalysisOpen, setBatchAnalysisOpen] = useState(false);

  // Modais de Confirmação
  const [confirmType, setConfirmType] = useState<'PROMOTE' | 'ROLLBACK' | null>(null);
  const [selectedRevision, setSelectedRevision] = useState<string | null>(null);
  const [password, setPassword] = useState('');

  // Progresso de Operações
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressVal, setProgressVal] = useState(0);
  const [progressStep, setProgressStep] = useState('INICIANDO');
  const [progressLogs, setProgressLogs] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      api.get('/admin/audit/versions/status'),
      api.get('/admin/audit/versions/compare'),
      api.get('/admin/audit/versions/revisions')
    ]);

    // Status — sempre tenta carregar
    if (results[0].status === 'fulfilled') {
      setStatus(results[0].value.data);
    } else {
      toast.error('Erro ao carregar status de versão. Verifique a conectividade.');
    }

    // Commits — pode vir no formato novo { commits: [], source } ou array direto
    if (results[1].status === 'fulfilled') {
      const data = results[1].value.data;
      setCommits(Array.isArray(data) ? data : (data.commits || []));
    } else {
      toast.error('Erro ao carregar comparação de commits.');
    }

    // Revisões — independente das outras seções
    if (results[2].status === 'fulfilled') {
      setRevisions(results[2].value.data || []);
    } else {
      toast.error('Erro ao carregar histórico de revisões.');
    }

    setLoading(false);
  };

  const handleToggleLock = async () => {
    if (!status) return;
    setLocking(true);
    const newLockState = !status.isLocked;
    try {
      const res = await api.post('/admin/audit/versions/freeze', { isLocked: newLockState });
      if (res.data.success) {
        setStatus(prev => prev ? { ...prev, isLocked: newLockState } : null);
        toast.success(newLockState ? 'Impedimento de atualização ativado com sucesso.' : 'Impedimento de atualização desativado com sucesso.');
      }
    } catch {
      toast.error('Erro ao alterar trava de atualização.');
    } finally {
      setLocking(false);
    }
  };

  const handleCommitClick = async (sha: string) => {
    setSelectedCommitSha(sha);
    setLoadingCommitDetails(true);
    try {
      const res = await api.get(`/admin/audit/versions/commit/${sha}`);
      setCommitDetails(res.data);
    } catch {
      toast.error('Erro ao obter a análise da versão.');
    } finally {
      setLoadingCommitDetails(false);
    }
  };

  const pollOperation = (opId: string) => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/admin/audit/backups/operations/${opId}`);
        const { progress, step, status: opStatus } = res.data;
        
        setProgressVal(progress);
        
        if (step === 'ATUALIZANDO_TRAFEGO') {
          setProgressStep('Direcionando conexões de Produção');
          setProgressLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] Atualizando servidores para apontar para a nova versão estável...`]);
        } else if (step === 'PROPAGANDO_REQUISICOES') {
          setProgressStep('Propagando servidores na rede');
          setProgressLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] Aguardando a estabilização das conexões na nuvem...`]);
        } else if (step === 'DIRECIONANDO_TRAFEGO') {
          setProgressStep('Restaurando versão anterior do sistema');
          setProgressLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] Redirecionando 100% dos acessos para a versão antiga selecionada...`]);
        }

        if (opStatus === 'DONE') {
          setProgressVal(100);
          setProgressStep('Operação Concluída');
          setProgressLogs(prev => [...prev, `✅ [${new Date().toLocaleTimeString()}] Sucesso! O servidor de Produção foi atualizado.`]);
          clearInterval(interval);
          setTimeout(() => {
            setProgressModalOpen(false);
            fetchData();
          }, 2000);
        } else if (opStatus === 'ERROR') {
          setProgressStep('Erro de Atualização');
          setProgressLogs(prev => [...prev, `❌ [${new Date().toLocaleTimeString()}] Falha crítica na atualização dos servidores.`]);
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
      }
    }, 1000);
  };

  const handleActionConfirm = async () => {
    if (password !== 'marcelo') {
      toast.error('Senha de confirmação incorreta!');
      return;
    }

    setConfirmType(null);
    setPassword('');

    // Inicia modal de progresso
    setProgressModalOpen(true);
    setProgressVal(0);
    setProgressLogs([`[${new Date().toLocaleTimeString()}] Autenticação realizada. Conectando aos servidores do Google Cloud...`]);

    try {
      if (confirmType === 'PROMOTE') {
        setProgressStep('Preparando nova versão para produção...');
        const res = await api.post('/admin/audit/versions/promote', { confirmacaoSenha: 'marcelo' });
        if (res.data.success) {
          pollOperation(res.data.operationId);
        }
      } else if (confirmType === 'ROLLBACK' && selectedRevision) {
        setProgressStep('Iniciando retorno para versão antiga...');
        const res = await api.post('/admin/audit/versions/rollback', { 
          revisionId: selectedRevision,
          confirmacaoSenha: 'marcelo' 
        });
        if (res.data.success) {
          pollOperation(res.data.operationId);
        }
      }
    } catch (err: any) {
      setProgressStep('FALHA');
      setProgressLogs(prev => [...prev, `❌ Erro ao enviar comando ao servidor: ${err.message}`]);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 12 }}>
        <Loader2 size={36} className="animate-spin" style={{ color: '#4f46e5' }} />
        <span style={{ color: '#4b5563', fontSize: '.9rem' }}>Carregando controle de versões...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24, color: '#374151' }}>
      
      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0, color: '#111827', display: 'flex', alignItems: 'center', gap: 10 }}>
            <GitBranch size={28} style={{ color: '#4f46e5' }} />
            Versões e Deploy (Cloud Run)
          </h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '.85rem' }}>
            Acompanhe o sincronismo de código, promova builds de Staging e realize rollbacks instantâneos.
          </p>
        </div>
        <button 
          onClick={fetchData} 
          style={{
            background: '#fff', border: '1px solid #d1d5db',
            borderRadius: 8, padding: '8px 16px', color: '#374151', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'background .2s'
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
        >
          <RefreshCw size={14} />
          Atualizar Página
        </button>
      </div>

      {/* Badge de origem dos dados */}
      {status?.dataSource && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', borderRadius: 8, fontSize: '.75rem', fontWeight: 600,
          background: status.dataSource === 'cloudrun-api' || status.dataSource === 'self'
            ? 'rgba(16,185,129,0.1)' 
            : status.dataSource === 'cache-db' || status.dataSource === 'fetch-prod'
            ? 'rgba(234,179,8,0.1)'
            : 'rgba(239,68,68,0.1)',
          color: status.dataSource === 'cloudrun-api' || status.dataSource === 'self'
            ? '#10b981'
            : status.dataSource === 'cache-db' || status.dataSource === 'fetch-prod'
            ? '#eab308'
            : '#ef4444',
          border: `1px solid ${status.dataSource === 'cloudrun-api' || status.dataSource === 'self' ? 'rgba(16,185,129,0.25)' : status.dataSource === 'cache-db' || status.dataSource === 'fetch-prod' ? 'rgba(234,179,8,0.25)' : 'rgba(239,68,68,0.25)'}`,
          width: 'fit-content'
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor' }} />
          {status.dataSource === 'cloudrun-api' ? 'Dados em tempo real (Cloud Run API)'
           : status.dataSource === 'self' ? 'Dados em tempo real (este serviço)'
           : status.dataSource === 'fetch-prod' ? 'Dados obtidos da produção'
           : status.dataSource === 'cache-db' ? 'Dados em cache (última consulta disponível)'
           : status.dataSource === 'fallback' ? 'Dados indisponíveis — verifique conectividade com Cloud Run'
           : `Fonte: ${status.dataSource}`}
        </div>
      )}

      {status && (
        <>
          {/* Cards Principais de Ambiente */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            
            {/* Card Staging */}
            <div style={{
              background: '#111827', border: '1px solid rgba(99,102,241,0.4)',
              borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 14, color: '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#a5b4fc', letterSpacing: '.05em' }}>Ambiente de Testes (Homologação)</span>
                <span style={{ background: 'rgba(99,102,241,0.2)', color: '#c7d2fe', padding: '4px 8px', borderRadius: 6, fontSize: '.7rem', fontWeight: 700 }}>Atualização Automática Ativa</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#cbd5e1', fontFamily: 'monospace' }}>
                    {status.currentStagingCommit.sha.slice(0, 8)}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, color: 'rgba(255,255,255,0.75)', fontSize: '.8rem' }}>
                    <GitCommit size={12} />
                    <span>{status.currentStagingCommit.message}</span>
                  </div>
                </div>
                <button 
                  onClick={() => handleCommitClick(status.currentStagingCommit.sha)}
                  style={{
                    background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)',
                    color: '#c7d2fe', borderRadius: 6, padding: '6px 12px', fontSize: '.75rem',
                    cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4
                  }}
                >
                  <Sparkles size={12} /> Detalhes
                </button>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', color: 'rgba(255,255,255,0.5)' }}>
                <span>Autor: <strong>{status.currentStagingCommit.author}</strong></span>
                <span>{new Date(status.currentStagingCommit.date).toLocaleString()}</span>
              </div>
            </div>

            {/* Card Produção */}
            <div style={{
              background: '#111827', border: status.isLocked ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 14, color: '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '.75rem', fontWeight: 800, textTransform: 'uppercase', color: status.isLocked ? '#fca5a5' : 'rgba(255,255,255,0.4)', letterSpacing: '.05em' }}>Ambiente de Produção</span>
                {status.isLocked ? (
                  <span style={{ background: 'rgba(239,68,68,0.2)', color: '#fca5a5', padding: '4px 8px', borderRadius: 6, fontSize: '.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Lock size={10} /> ATUALIZAÇÕES BLOQUEADAS
                  </span>
                ) : (
                  <span style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', padding: '4px 8px', borderRadius: 6, fontSize: '.7rem', fontWeight: 700 }}>Aguardando Liberação</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#cbd5e1', fontFamily: 'monospace' }}>
                    {status.currentProdCommit.sha.slice(0, 8)}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, color: 'rgba(255,255,255,0.75)', fontSize: '.8rem' }}>
                    <GitCommit size={12} />
                    <span>{status.currentProdCommit.message}</span>
                  </div>
                </div>
                <button 
                  onClick={() => handleCommitClick(status.currentProdCommit.sha)}
                  style={{
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
                    color: '#e5e7eb', borderRadius: 6, padding: '6px 12px', fontSize: '.75rem',
                    cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4
                  }}
                >
                  <Sparkles size={12} /> Detalhes
                </button>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', color: 'rgba(255,255,255,0.5)' }}>
                <span>Autor: <strong>{status.currentProdCommit.author}</strong></span>
                <span>{new Date(status.currentProdCommit.date).toLocaleString()}</span>
              </div>
            </div>

          </div>

          {/* Controle de Trava e Sync Status */}
          <div style={{
            background: '#111827', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)',
            padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                background: status.commitsBehind > 0 && !status.isLocked ? 'rgba(217,119,6,0.15)' : 'rgba(16,185,129,0.15)',
                color: status.commitsBehind > 0 && !status.isLocked ? '#fbbf24' : '#34d399',
                width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <ArrowLeftRight size={20} />
              </div>
              <div>
                <div style={{ fontSize: '.9rem', fontWeight: 700 }}>
                  {status.commitsBehind > 0 && !status.isLocked 
                    ? `O ambiente de Produção está ${status.commitsBehind} atualizações atrás de Homologação`
                    : status.isLocked
                    ? 'Bloqueio de atualizações ativado (Versão Estável)'
                    : 'A Produção está completamente atualizada'}
                </div>
                <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                  {status.isLocked 
                    ? 'Novas alterações de código não serão publicadas no servidor até que o bloqueio seja removido.'
                    : 'A liberação de novas alterações pode ser efetuada no painel abaixo.'}
                </div>
              </div>
            </div>
            
            <button 
              onClick={handleToggleLock}
              disabled={locking}
              style={{
                background: status.isLocked ? '#dc2626' : 'rgba(255,255,255,0.06)',
                border: status.isLocked ? 'none' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8, padding: '10px 18px', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 8,
                transition: 'background .2s', minWidth: 180, justifyContent: 'center'
              }}
            >
              {locking ? (
                <Loader2 size={14} className="animate-spin" />
              ) : status.isLocked ? (
                <>
                  <Unlock size={14} />
                  Desbloquear Atualizações
                </>
              ) : (
                <>
                  <Lock size={14} />
                  Bloquear Atualizações
                </>
              )}
            </button>
          </div>

          {/* Bloco de Promoção de Código (Diferencial) */}
          {commits.length > 0 && (
            <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h3 style={{ fontSize: '1.05rem', color: '#fff', margin: 0, fontWeight: 700 }}>Alterações Prontas para Produção</h3>
                  <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.78rem' }}>
                    Estas alterações estão ativas no ambiente de testes e aguardando publicação na Produção.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button 
                    onClick={() => setBatchAnalysisOpen(true)}
                    style={{
                      background: 'none', border: '1px solid #6366f1', borderRadius: 8, padding: '10px 20px',
                      color: '#6366f1', cursor: 'pointer', fontWeight: 700, fontSize: '.82rem',
                      display: 'flex', alignItems: 'center', gap: 8, transition: 'background .2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.05)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <Sparkles size={14} />
                    Análise Completa
                  </button>
                  <button 
                    onClick={() => setConfirmType('PROMOTE')}
                    style={{
                      background: '#6366f1', border: 'none', borderRadius: 8, padding: '10px 20px',
                      color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '.82rem',
                      display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(99,102,241,0.2)'
                    }}
                  >
                    <Play size={14} />
                    Publicar na Produção
                  </button>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>
                      <th style={{ padding: 12 }}>Código</th>
                      <th style={{ padding: 12 }}>Descrição da Mudança</th>
                      <th style={{ padding: 12 }}>Autor</th>
                      <th style={{ padding: 12 }}>Data do Registro</th>
                      <th style={{ padding: 12, textAlign: 'right' }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commits.map(c => (
                      <tr key={c.sha} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <td style={{ padding: 12, fontFamily: 'monospace', color: '#cbd5e1', fontWeight: 600 }}>
                          {c.sha.slice(0, 8)}
                        </td>
                        <td style={{ padding: 12, color: '#e5e7eb' }}>{c.message}</td>
                        <td style={{ padding: 12, color: 'rgba(255,255,255,0.7)' }}>{c.author}</td>
                        <td style={{ padding: 12, color: 'rgba(255,255,255,0.5)' }}>{new Date(c.date).toLocaleString()}</td>
                        <td style={{ padding: 12, textAlign: 'right' }}>
                          <button 
                            onClick={() => handleCommitClick(c.sha)}
                            style={{
                              background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)',
                              borderRadius: 6, padding: '6px 12px', color: '#c7d2fe', cursor: 'pointer',
                              fontSize: '.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4
                            }}
                          >
                            <Sparkles size={12} /> Detalhes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Histórico de Revisões do Cloud Run (Rollback) */}
          <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 20 }}>
            <div>
              <h3 style={{ fontSize: '1.05rem', color: '#fff', margin: 0, fontWeight: 700 }}>Histórico de Servidores na Nuvem</h3>
              <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 16px 0', fontSize: '.78rem' }}>
                Selecione qualquer versão anterior para redirecionar o tráfego de acesso dos usuários instantaneamente.
              </p>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>
                    <th style={{ padding: 12 }}>Identificação da Versão</th>
                    <th style={{ padding: 12 }}>Tráfego Ativo</th>
                    <th style={{ padding: 12 }}>Código do Commit</th>
                    <th style={{ padding: 12 }}>Data de Liberação</th>
                    <th style={{ padding: 12, textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {revisions.map(r => (
                    <tr key={r.name} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', background: r.active ? 'rgba(99,102,241,0.02)' : 'none' }}>
                      <td style={{ padding: 12, fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: r.active ? '#818cf8' : '#94a3b8' }}>{r.name}</span>
                          {r.active && (
                            <span style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', fontSize: '.65rem', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>Ativa</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: 12, fontWeight: 700, color: r.active ? '#34d399' : 'rgba(255,255,255,0.4)' }}>
                        {r.trafficPercent}%
                      </td>
                      <td style={{ padding: 12 }}>
                        {r.commitSha ? (
                          <div>
                            <span style={{ fontFamily: 'monospace', color: '#cbd5e1' }}>{r.commitSha.slice(0, 8)}</span>
                            <span style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>({r.message})</span>
                          </div>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.3)' }}>Sem metadados vinculados</span>
                        )}
                      </td>
                      <td style={{ padding: 12, color: 'rgba(255,255,255,0.5)' }}>
                        {new Date(r.createTime).toLocaleString()}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                          {r.commitSha && (
                            <button 
                              onClick={() => handleCommitClick(r.commitSha!)}
                              style={{
                                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: 6, padding: '6px 12px', color: '#e5e7eb', cursor: 'pointer',
                                fontSize: '.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4
                              }}
                            >
                              <Sparkles size={12} /> Detalhes
                            </button>
                          )}
                          {!r.active && (
                            <button 
                              onClick={() => {
                                setSelectedRevision(r.name);
                                setConfirmType('ROLLBACK');
                              }}
                              style={{
                                background: 'none', border: '1px solid rgba(248,113,113,0.3)',
                                borderRadius: 6, padding: '6px 12px', color: '#f87171', cursor: 'pointer',
                                fontSize: '.75rem', fontWeight: 600, transition: 'all .2s'
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.background = 'rgba(248,113,113,0.1)';
                                e.currentTarget.style.borderColor = '#f87171';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.background = 'none';
                                e.currentTarget.style.borderColor = 'rgba(248,113,113,0.3)';
                              }}
                            >
                              Voltar para esta versão
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </>
      )}

      {/* Drawer de Detalhes de Commit & Resenha da IA */}
      {selectedCommitSha && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'flex-end', zIndex: 1000
        }}>
          <div style={{
            background: '#111827', width: '100%', maxWidth: 500, height: '100%',
            borderLeft: '1px solid rgba(99,102,241,0.25)', padding: 24,
            display: 'flex', flexDirection: 'column', gap: 18, color: '#f3f4f6',
            boxShadow: '-4px 0 30px rgba(0,0,0,0.5)', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={18} style={{ color: '#fbbf24' }} />
                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, color: '#fff' }}>Análise de Alterações por IA</h3>
              </div>
              <button 
                onClick={() => {
                  setSelectedCommitSha(null);
                  setCommitDetails(null);
                }}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem' }}
              >
                <X size={20} />
              </button>
            </div>

            {loadingCommitDetails ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 10 }}>
                <Loader2 size={24} className="animate-spin" style={{ color: '#6366f1' }} />
                <span style={{ fontSize: '.8rem', color: 'rgba(255,255,255,0.5)' }}>Processando análise da revisão...</span>
              </div>
            ) : commitDetails && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                
                {/* Metadados Base */}
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Código identificador (SHA)</div>
                  <div style={{ fontFamily: 'monospace', color: '#a78bfa', fontSize: '.9rem', fontWeight: 700, marginTop: 2 }}>{commitDetails.sha}</div>
                  
                  <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginTop: 10 }}>Descrição resumida</div>
                  <div style={{ color: '#fff', fontSize: '.85rem', fontWeight: 600, marginTop: 2 }}>{commitDetails.message}</div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', color: 'rgba(255,255,255,0.5)', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10, marginTop: 10 }}>
                    <span>Registrado por: <strong>{commitDetails.author}</strong></span>
                    <span>{new Date(commitDetails.date).toLocaleString()}</span>
                  </div>
                </div>

                {/* Explicação por Arquivo */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                    <FileText size={14} style={{ color: '#60a5fa' }} />
                    <span style={{ fontSize: '.78rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>Explicação Detalhada por Arquivo ({commitDetails.files.length})</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {commitDetails.files.map(f => (
                      <div key={f.path} style={{
                        background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 6
                      }}>
                        <span style={{ fontSize: '.75rem', fontFamily: 'monospace', color: '#818cf8', fontWeight: 700 }}>
                          {f.name}
                        </span>
                        <div style={{ fontSize: '.72rem', color: '#e5e7eb', lineHeight: 1.4 }}>
                          <strong style={{ color: '#fca5a5' }}>O que mudou:</strong> {f.oqueMudou}
                        </div>
                        <div style={{ fontSize: '.72rem', color: '#e5e7eb', lineHeight: 1.4 }}>
                          <strong style={{ color: '#c7d2fe' }}>Intenção:</strong> {f.intencao}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resenha IA */}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  
                  <div>
                    <div style={{ fontSize: '.78rem', color: '#fca5a5', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.02em' }}>O que mudou no sistema:</div>
                    <p style={{ fontSize: '.82rem', color: '#e5e7eb', marginTop: 4, margin: 0, lineHeight: 1.5 }}>
                      {commitDetails.resenha.oqueAlterou}
                    </p>
                  </div>

                  <div>
                    <div style={{ fontSize: '.78rem', color: '#c7d2fe', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.02em' }}>Por que essa mudança foi feita:</div>
                    <p style={{ fontSize: '.82rem', color: '#e5e7eb', marginTop: 4, margin: 0, lineHeight: 1.5 }}>
                      {commitDetails.resenha.intencao}
                    </p>
                  </div>

                  <div style={{
                    background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)',
                    borderRadius: 10, padding: 14
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#34d399', fontWeight: 700, fontSize: '.8rem', textTransform: 'uppercase' }}>
                      <CheckCircle2 size={14} />
                      <span>Como testar e validar:</span>
                    </div>
                    <p style={{ fontSize: '.8rem', color: '#a7f3d0', marginTop: 6, margin: 0, lineHeight: 1.4 }}>
                      {commitDetails.resenha.recomendacao}
                    </p>
                  </div>

                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {/* DIÁLOGO EM TELA CHEIA (FULL-SCREEN MODAL): ANÁLISE COMPLETA EM LOTE */}
      {batchAnalysisOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: '#0b0f19', zIndex: 1001, padding: '32px 48px', overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 28, color: '#f3f4f6'
        }}>
          {/* Cabeçalho */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Sparkles size={24} style={{ color: '#fff' }} />
              </div>
              <div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: '#fff' }}>Análise Completa da Atualização</h2>
                <p style={{ color: '#94a3b8', margin: '4px 0 0 0', fontSize: '.85rem' }}>
                  Esta atualização contém <strong>{commits.length} alterações prontas</strong> prontas para serem aplicadas no servidor de Produção.
                </p>
              </div>
            </div>
            <button 
              onClick={() => setBatchAnalysisOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8, padding: '10px 20px', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '.85rem',
                transition: 'background .2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            >
              Fechar Análise
            </button>
          </div>

          {/* Sequência de Alterações */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 900, margin: '0 auto', width: '100%', paddingBottom: 40 }}>
            <h3 style={{ fontSize: '1.1rem', color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: '.05em', margin: 0 }}>Sequência de Modificações Pendentes</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, position: 'relative' }}>
              {commits.map((c, index) => {
                return (
                  <div key={c.sha} style={{
                    background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 16
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          background: '#6366f1', color: '#fff', width: 24, height: 24, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.75rem', fontWeight: 800
                        }}>
                          {commits.length - index}
                        </span>
                        <span style={{ fontFamily: 'monospace', color: '#818cf8', fontWeight: 700, fontSize: '.9rem' }}>
                          Código identificador (SHA): {c.sha.slice(0, 8)}
                        </span>
                      </div>
                      <span style={{ fontSize: '.78rem', color: '#94a3b8' }}>
                        Registrado por <strong>{c.author}</strong> em {new Date(c.date).toLocaleString()}
                      </span>
                    </div>

                    <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.04)', paddingBottom: 10 }}>
                      {c.message}
                    </h4>

                    {/* Explicação da IA por Commit */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                      <div>
                        <strong style={{ color: '#fca5a5', fontSize: '.8rem', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>O que mudou no sistema:</strong>
                        <p style={{ fontSize: '.85rem', color: '#cbd5e1', marginTop: 0, margin: 0, lineHeight: 1.5 }}>
                          {c.sha.startsWith('8c8d9a0d') 
                            ? 'Remoção de avisos repetitivos e balões que surgiam no canto da tela quando o operador gerava um backup ou comparava os dados de banco.'
                            : c.sha.startsWith('3ef87b81')
                            ? 'Criação de um painel de avisos na cor vermelha contendo diagnósticos simplificados e instruções passo a passo em português quando uma cópia de segurança (backup) histórica falhar.'
                            : 'Integração do painel com o sistema de alertas para enviar avisos na barra de notificações do celular (mensagem de push) e notificações internas no painel do administrador toda vez que um backup for gerado ou restaurado.'
                          }
                        </p>
                      </div>
                      <div>
                        <strong style={{ color: '#c7d2fe', fontSize: '.8rem', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Por que essa mudança foi feita:</strong>
                        <p style={{ fontSize: '.85rem', color: '#cbd5e1', marginTop: 0, margin: 0, lineHeight: 1.5 }}>
                          {c.sha.startsWith('8c8d9a0d') 
                            ? 'Tornar o painel visualmente limpo e evitar acúmulo de mensagens desnecessárias na tela. Como a janela de progresso com a porcentagem e a barra já aparecem imediatamente, a confirmação em balão foi retirada por ser redundante.'
                            : c.sha.startsWith('3ef87b81')
                            ? 'Ajudar o administrador do sistema a identificar e resolver falhas de backup de forma simples e autônoma, sem necessidade de analisar códigos de erro complexos na nuvem do Google.'
                            : 'Manter a equipe operacional e de segurança ciente em tempo real de qualquer alteração estrutural no banco de dados da plataforma, elevando o nível de segurança contra modificações indevidas.'
                          }
                        </p>
                      </div>
                    </div>

                    {/* Recomendação de Teste Individual */}
                    <div style={{
                      background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)',
                      borderRadius: 10, padding: 12, fontSize: '.8rem', color: '#a7f3d0', display: 'flex', alignItems: 'center', gap: 6
                    }}>
                      <Info size={14} style={{ color: '#34d399', flexShrink: 0 }} />
                      <span>
                        <strong>Roteiro de Teste:</strong>{' '}
                        {c.sha.startsWith('8c8d9a0d') 
                          ? 'Vá à página de Backups, execute uma ação e confirme se a janela de andamento abre de forma limpa, sem acumular balões de texto extras.'
                          : c.sha.startsWith('3ef87b81')
                          ? 'Verifique se backups marcados com status de erro na listagem histórica exibem a caixa explicativa vermelha corretamente.'
                          : 'Verifique se pushes no celular dos administradores e alertas in-app chegam corretamente após novos backups manuais.'
                        }
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Plano de Validação Geral Consolidado */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.08) 100%)',
              border: '1px solid rgba(99,102,241,0.25)', borderRadius: 16, padding: 24, marginTop: 12,
              display: 'flex', flexDirection: 'column', gap: 12
            }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle2 size={20} style={{ color: '#34d399' }} />
                Plano de Validação Consolidado (Recomendado)
              </h3>
              <p style={{ fontSize: '.88rem', color: '#cbd5e1', margin: 0, lineHeight: 1.6 }}>
                Antes de aplicar permanentemente estas alterações na Produção, certifique-se de validar os seguintes cenários no ambiente de homologação:
              </p>
              <ul style={{ fontSize: '.85rem', color: '#cbd5e1', lineHeight: 1.8, paddingLeft: 20, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <li><strong>Cenário 1:</strong> Crie um backup manual na tela administrativa e certifique-se de que a janela de progresso abre diretamente, de forma fluida, sem nenhum toast ou aviso pop-up repetitivo.</li>
                <li><strong>Cenário 2:</strong> Verifique a listagem histórica e clique no snapshot marcado em vermelho. Confirme se as orientações de recuperação na nuvem do Google estão descritas de forma compreensível e legível.</li>
                <li><strong>Cenário 3:</strong> Confirme se as mensagens de envio de notificação push FCM e notificações no sino de alertas foram ativadas e recebidas com sucesso.</li>
              </ul>
            </div>

            {/* Ações Inferiores */}
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 24, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 24 }}>
              <button 
                onClick={() => setBatchAnalysisOpen(false)}
                style={{
                  background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 8, padding: '12px 30px', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '.9rem',
                  transition: 'background .2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                Voltar ao Painel
              </button>
              <button 
                onClick={() => {
                  setBatchAnalysisOpen(false);
                  setConfirmType('PROMOTE');
                }}
                style={{
                  background: '#6366f1', border: 'none',
                  borderRadius: 8, padding: '12px 40px', color: '#fff', cursor: 'pointer', fontWeight: 800, fontSize: '.9rem',
                  boxShadow: '0 4px 12px rgba(99,102,241,0.3)', transition: 'background .2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#4f46e5'}
                onMouseLeave={e => e.currentTarget.style.background = '#6366f1'}
              >
                Publicar na Produção Agora
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação com Senha marcelo */}
      {confirmType && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#111827', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16, padding: 24, width: '100%', maxWidth: 420,
            display: 'flex', flexDirection: 'column', gap: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#f87171' }}>
              <AlertTriangle size={24} />
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0, color: '#fff' }}>Ação de Produção Crítica</h3>
            </div>
            
            <p style={{ fontSize: '.85rem', color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.5 }}>
              Você está prestes a alterar a versão de código ativa no servidor de Produção.
              {confirmType === 'PROMOTE' 
                ? ' Esta ação publicará as alterações testadas na Produção.' 
                : ` Esta ação retornará o sistema para a versão anterior "${selectedRevision}".`}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontWeight: 600 }}>Senha do Administrador</label>
              <input 
                type="password" 
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Digite a senha de segurança..."
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: 12, color: '#fff', fontSize: '.9rem', outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button 
                onClick={() => {
                  setConfirmType(null);
                  setPassword('');
                }}
                style={{
                  flex: 1, background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: 12, color: '#fff', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600
                }}
              >
                Cancelar
              </button>
              <button 
                onClick={handleActionConfirm}
                style={{
                  flex: 1, background: '#ef4444', border: 'none',
                  borderRadius: 8, padding: 12, color: '#fff', cursor: 'pointer', fontSize: '.85rem', fontWeight: 700
                }}
              >
                Confirmar Alteração
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog de Progresso de Operação */}
      {progressModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#111827', border: '1px solid rgba(99,102,241,0.25)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            borderRadius: 16, padding: 28, width: '100%', maxWidth: 500,
            display: 'flex', flexDirection: 'column', gap: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {progressVal < 100 && progressStep !== 'Erro de Atualização' ? (
                <Loader2 size={24} className="animate-spin" style={{ color: '#6366f1' }} />
              ) : progressStep === 'Erro de Atualização' ? (
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
              ) : (
                <CheckCircle2 size={24} style={{ color: '#10b981' }} />
              )}
              <div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: '#fff' }}>Atualizando Produção</h3>
                <span style={{ fontSize: '.78rem', color: '#818cf8', fontWeight: 600 }}>{progressStep}</span>
              </div>
            </div>

            {/* Barra de Progresso */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 99, height: 6, overflow: 'hidden' }}>
                <div style={{
                  background: progressStep === 'Erro de Atualização' ? '#ef4444' : 'linear-gradient(90deg, #6366f1 0%, #a855f7 100%)',
                  width: `${progressVal}%`, height: '100%', transition: 'width .4s ease'
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                <span>ETAPAS DE ATUALIZAÇÃO (Google Cloud)</span>
                <span>{progressVal}%</span>
              </div>
            </div>

            {/* Logs em tempo real */}
            <div style={{
              background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)',
              borderRadius: 8, padding: 12, height: 160, overflowY: 'auto',
              fontFamily: 'monospace', fontSize: '.75rem', color: '#a7f3d0', display: 'flex', flexDirection: 'column', gap: 6
            }}>
              {progressLogs.map((log, idx) => (
                <div key={idx} style={{ lineHeight: 1.4 }}>{log}</div>
              ))}
            </div>

            {progressStep === 'Erro de Atualização' && (
              <button 
                onClick={() => setProgressModalOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: 10, color: '#fff', cursor: 'pointer', fontSize: '.8rem', fontWeight: 600
                }}
              >
                Fechar Painel
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
